import { useState } from 'react';

import { useBreakpointWinnerCampaign } from '@/hooks/useBreakpointWinnerCampaign';

// Solana Breakpoint brand color (purple/gradient)
const BREAKPOINT_COLOR = '#9945FF';

function formatTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) return 'Campaign ended';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) return `${days}d ${hours}h left`;
  return `${hours}h left`;
}

export function ChestBreakpointWinnerBonusIndicator() {
  const { campaignData, isLoading } = useBreakpointWinnerCampaign();
  const [isHovered, setIsHovered] = useState(false);

  // Only show for Breakpoint winners with remaining bonus
  if (isLoading || !campaignData?.isActive || !campaignData?.isBreakpointWinner || campaignData?.remainingBonus <= 0) {
    return null;
  }

  const timeRemaining = formatTimeRemaining(campaignData.endDate);
  const progress = `${campaignData.userBonusEarned.toLocaleString()}/${campaignData.maxBonus.toLocaleString()}`;

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Breakpoint Winner 2x Badge */}
      <div
        className="cursor-pointer transition-all duration-200 flex items-center gap-2 px-3 py-1.5 rounded-full"
        style={{
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          background: 'linear-gradient(135deg, rgba(153, 69, 255, 0.15) 0%, rgba(20, 241, 149, 0.15) 100%)',
          border: `1px solid ${BREAKPOINT_COLOR}`,
        }}
      >
        <span style={{ fontSize: '14px' }}>🏆</span>
        <span
          className="text-sm font-bold"
          style={{ color: BREAKPOINT_COLOR, fontFamily: 'Outfit, sans-serif' }}
        >
          VIP 2x
        </span>
      </div>

      {/* Hover Tooltip */}
      {isHovered && (
        <div
          className="absolute top-full left-0 mt-2 w-72 p-4 rounded-xl shadow-2xl z-50"
          style={{
            backgroundColor: '#343434',
            border: `2px solid ${BREAKPOINT_COLOR}`,
            boxShadow: `0 0 20px rgba(153, 69, 255, 0.3)`,
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span style={{ fontSize: '20px' }}>🏆</span>
            <h3
              className="text-base font-bold"
              style={{ color: BREAKPOINT_COLOR, fontFamily: 'Outfit, sans-serif' }}
            >
              Breakpoint Winner
            </h3>
          </div>

          <div className="space-y-3">
            <p className="text-sm" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
              You're earning <strong style={{ color: BREAKPOINT_COLOR }}>2x points</strong> on every chest opening!
            </p>

            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-xs mb-1" style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>
                <span>Bonus Progress</span>
                <span style={{ color: BREAKPOINT_COLOR, fontWeight: 600 }}>{progress} pts</span>
              </div>
              <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'rgba(153, 69, 255, 0.2)' }}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${(campaignData.userBonusEarned / campaignData.maxBonus) * 100}%`,
                    background: `linear-gradient(90deg, ${BREAKPOINT_COLOR} 0%, #14F195 100%)`,
                  }}
                />
              </div>
            </div>

            {/* Time Remaining */}
            <div className="flex justify-between items-center text-xs pt-2" style={{ borderTop: '1px solid rgba(153, 69, 255, 0.3)' }}>
              <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Expires</span>
              <span style={{ color: BREAKPOINT_COLOR, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                {timeRemaining}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
